import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Module } from './models/module';
import { AuthService } from 'src/app/services/auth.service';
import { FeatureFlagService } from './shared/services/feature-flag.service';
import { AppConfigurationService, AppSettings } from './shared/services/app-configuration.service';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { Observable, Subject, combineLatest } from 'rxjs'
import { TenantState } from './store/state/tenant.state';
import { Tenant } from './models/tenant';
import { UserService } from './users/user.service';
import { AuthorizationService } from './services/authorization.service';
import { MaintainTenantUsers } from './models/constant/right';
import { DrawerService } from './shared/drawer/drawer.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { VehicleLayoutService } from './fleet/vehicle-layout/vehicle-layout.service';
import { PreviousRouteService } from './services/previous-route.service';
import { AlertRuleDetailService } from './diagnostics/component/alert-rule-detail/alert-rule-detail.service';
import { TenantService } from './tenant/tenant.service';
import { PartTypeService } from './shared/services/part-type.service';
import { User } from 'oidc-client-ts';
import { TenantStateService } from './shared/services/tenant.state.service';
import { DOCUMENT } from '@angular/common';
import { sidebarMode } from './shared/services/spa.routing.service';
import { findRouteData } from './shared/handler/common-functions.util';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @Select(TenantState.getCurrentTenant) getTenant$: Observable<Tenant>;
  @Select(TenantState.getCurrentModule) getCurrentModule$: Observable<Module>;
  public title = 'FleetHD';
  public isLoggedIn = false;
  isCollapsed = true;
  isSidebarCollapsed: boolean;
  hideSidebar = false;
  private routeHidesSidebar = false;
  private currentModule: Module;
  private destroy$ = new Subject();
  private currentTenant: Tenant;
  // Fallback drawer offset used only when the header isn't measurable yet;
  // matches the rendered .fhd-nav-container height (51px content + 8px vertical padding).
  private minHeaderHeight: number = 59;
  statusPageEmbed: string;

  constructor(
    private readonly authService: AuthService,
    private readonly authorizationService: AuthorizationService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly appConfigurationService: AppConfigurationService,
    private readonly userService: UserService,
    private readonly drawerService: DrawerService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly analyticsService: AnalyticsService,
    private readonly vehicleLayoutService: VehicleLayoutService,
    private readonly alertRuleService: AlertRuleDetailService,
    private readonly tenantService: TenantService,
    private readonly partTypeService: PartTypeService,
    private readonly tenantStateService: TenantStateService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private readonly previousRouteService: PreviousRouteService,//do not remove this, the constructor starts a subscription
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {
  }

  ngAfterViewInit(): void {
    // Pin the drawer to the measured header height. Fall back to a constant
    // only if the header isn't measurable yet (offsetHeight 0/undefined) — do
    // NOT floor to the constant, or the drawer drops below a shorter header.
    const headerHeight = document.getElementsByTagName('app-header')[0]?.['offsetHeight'] ?? 0;
    const headerTopOffset = headerHeight > 0 ? headerHeight : this.minHeaderHeight;
    this.drawerService.registerDrawer(
      this.viewContainerRef,
      { topOffset: headerTopOffset });

    this.analyticsService.init();
    this.analyticsService.trackPageViews()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  ngOnInit(): void {
    // Drive hideSidebar only from NavigationEnd so the route tree is fully
    // resolved before we walk it. A synchronous startWith() here would run
    // before the initial navigation completes and miss the hideSidebar data
    // entry, causing the sidebar to flash visible on cold loads of routes
    // that want it hidden (e.g. /serviceCenter).
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.routeHidesSidebar = !!findRouteData<boolean>(this.activatedRoute.snapshot, 'hideSidebar');
      this.updateHideSidebar();
    });

    this.getCurrentModule$
      .pipe(takeUntil(this.destroy$))
      .subscribe(module => {
        this.currentModule = module;
        this.updateHideSidebar();
      });

    this.authService.user$.pipe(
      filter((user): user is User => user != null),
      take(1)
    ).subscribe(user => {
      this.isLoggedIn = true;
      this.hydrateApplication();
    });

    combineLatest(
      [
        this.authService.user$.pipe(filter((user): user is User => user != null)),
        this.getTenant$
      ])
      .pipe(
        filter(([user, tenant]: [User, Tenant]) => tenant?.tenantId != this.currentTenant?.tenantId),
        takeUntil(this.destroy$),
        tap(([user, tenant]: [User, Tenant]) => {
          this.currentTenant = tenant;
        }),
        filter(([user, tenant]: [User, Tenant]) => tenant?.externalId != null && tenant?.externalId !== ''),
        switchMap(([user]) => {
          const sources: Observable<any>[] = [];

          sources.push(this.tenantStateService.setModules());
          if (this.authorizationService.hasRight(
            user.profile,
            MaintainTenantUsers)) {
            sources.push(this.userService.getUsers());
          }
          else {
            sources.push(this.userService.getUser());
          }
          sources.push(this.vehicleLayoutService.getMapping());
          sources.push(this.alertRuleService.getAll());
          sources.push(this.tenantService.getFeatures());
          sources.push(this.partTypeService.getAll());

          return combineLatest(sources)
        }))
      .subscribe();
  }

  hydrateApplication(): void {
    this.appConfigurationService.settings$
      .pipe(take(1))
      .subscribe((settings: AppSettings) => {
        //change title
        document.title = settings.siteTitle;
        //change icon
        var link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        link.href = `${settings.contentDeliveryNetworkUrl}/${settings.siteIcon}`;

        let forceLogout = localStorage.getItem('forceLogout');
        if (forceLogout === null) {
          localStorage.setItem('forceLogout', settings.forceLogout);
        }
        else if (settings.forceLogout !== forceLogout) {
          localStorage.setItem('forceLogout', settings.forceLogout);
          this.tenantStateService.clearTenantState();
          this.authService.logout();
        }
        this.beginFeaturePolling(settings.featureFlagInterval, settings.idleTimeoutPeriod);
        if (settings.statusPageEmbedScript) {
          const script = this.renderer.createElement('script');
          script.type = 'text/javascript';
          script.src = settings.statusPageEmbedScript;
          this.renderer.appendChild(this.document.body, script);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  logout = (event) => {
    event.preventDefault();
    const profile = this.authService.getUserProfile();
    this.userService.logout().subscribe(res => {
      this.authService.logout();
      this.featureFlagService.cancelPolling();
    },
      err => {
        this.authService.logout();
        this.featureFlagService.cancelPolling();
      });
  }

  updateSize(event: any): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  // Route-data hideSidebar covers routes outside the menu config (e.g. Service Center).
  private updateHideSidebar(): void {
    const moduleHides = this.currentModule != null && sidebarMode(this.currentModule) === 'none';
    this.hideSidebar = this.routeHidesSidebar || moduleHides;
  }

  beginFeaturePolling(featureFlagInterval: number, idleTimeoutPeriod: number): void {
    if (this.isLoggedIn && !this.featureFlagService.checkIsPolling()) {
      this.featureFlagService.beginPolling(featureFlagInterval, idleTimeoutPeriod);
    }
  }

}
